import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { FaTimes } from "react-icons/fa";
import { selectStyles } from "../ManageUserTab/types/SelectStyles";
import {
  IOption,
  IOldTeam,
  IOldUser,
  IRole,
  IRegulatorData,
  ITeam,
} from "../../interfaces/team.interface";
import CreatableSelect from "react-select/creatable";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchTeamData } from "../ManageUserTab/services/fetchTeamData.service";
import { DeleteButton } from "../../../../components/Admin";
import { useUserContext } from "../../../../context/UserContext";

interface ICreateTeamModalProps {
  setShowModal: (state: boolean) => void;
  handleCreateEditTeam: (team: ITeam, isUpdate: boolean) => void;
  adminOptions: { value: string; label: string }[];
  memberOptions: { value: string; label: string }[];
  regulatorOptions: { value: string; label: string }[];
  teamOptions: {
    value: string;
    label: string;
    members: { value: string; label: string }[];
  }[];
  roleOptions: { value: string; label: string }[];
  darkMode: boolean;
  teamData: any;
}

const CreateEditTeamModal: React.FC<ICreateTeamModalProps> = ({
  setShowModal,
  adminOptions,
  memberOptions,
  handleCreateEditTeam,
  regulatorOptions,
  roleOptions,
  teamOptions,
  darkMode,
  teamData,
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  const [selectedTeam, setSelectedTeam] = useState<IOldTeam | null>(null);
  const [teamName, setTeamName] = useState<string>("");
  const [requestsLimit, setRequestsLimit] = useState<string>("80");
  const [selectedAdmins, setSelectedAdmins] = useState<IOldUser[]>([]);
  const [selectedRegulator, setSelectedRegulator] = useState<
    IRegulatorData[] | null
  >(null);
  const [addedMembers, setAddedMembers] = useState<IRole[]>([]);
  const teamId = selectedTeam
    ? Number((selectedTeam as any).id ?? (selectedTeam as any).value)
    : undefined;
  const queryClient = useQueryClient();
  const DEFAULT_ROLE_LABEL = roleOptions[2]?.label ?? "Viewer";
  const DEFAULT_ROLE_ID = Number(roleOptions[2]?.value ?? 2);

  const teamMembersOriginRef = useRef<Map<number, Set<string>>>(new Map());
  const prevSelectedTeamIdRef = useRef<number | null>(null);

  const { data: teamDataUserList, isFetching } = useQuery({
    queryKey: ["teamDataUserList", selectedTeam?.id],
    queryFn: () => fetchTeamData(Number(selectedTeam!.id)),
    enabled: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (teamData) {
      setTeamName(teamData.name);
      if (teamData.requests_limit != null) {
        setRequestsLimit(String(teamData.requests_limit));
      }
      setSelectedAdmins(
        teamData.roles
          .filter((role: IRole) => role.right === "Admin")
          .map((role: IRole) => ({
            value: role.user.email,
            label: role.user.name || role.user.email,
          })),
      );
      setSelectedRegulator(teamData.regulators);
      setAddedMembers(teamData.roles);
    }
  }, [teamData]);

  const getSelectStyles = (customStyles = {}) => ({
    ...selectStyles(darkMode),
    input: (base: any) => ({
      ...base,
      color: darkMode ? "#FFFFFF" : "#000000",
    }),
    option: (base: any, { isFocused }: any) => ({
      ...base,
      backgroundColor: isFocused ? "#86BC25" : darkMode ? "#374151" : "#FFFFFF",
      color: darkMode ? "#FFFFFF" : "#000000",
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: darkMode ? "#43B02A" : "#26890D",
    }),
    multiValueLabel: (base: any) => ({ ...base, color: "#e2e8f0" }),
    ...customStyles,
  });

  const handleAddMember = (member: IOldUser | null) => {
    if (!member || !member.label || !member.value) return;

    if (!addedMembers.some((m) => m.user.email === member.value)) {
      const name =
        typeof member.label === "string"
          ? member.label.split("<")[0].trim()
          : member.value;

      const newMember: IRole = {
        id: null,
        right: roleOptions[2]?.label ?? "Viewer",
        right_id: Number(roleOptions[2]?.value ?? 2),
        user: {
          name,
          email: member.value,
          id: 1,
        },
        created_at: null,
      };
      setAddedMembers([...addedMembers, newMember]);
    }
  };

  const handleRemoveMember = (member: IRole) => {
    setAddedMembers((prev) =>
      prev.filter((m) => m.user.email !== member.user.email),
    );
    setSelectedAdmins((prev) =>
      prev.filter((admin) => admin.value !== member.user.email),
    );
  };

  const handleCopyTeamMembers = async (opt: IOldTeam | null) => {
    setSelectedTeam(opt);

    if (!opt) {
      const prevId = prevSelectedTeamIdRef.current;
      if (prevId != null) {
        const prevEmails =
          teamMembersOriginRef.current.get(prevId) ?? new Set<string>();
        setAddedMembers((prev) =>
          prev.filter(
            (m) =>
              !prevEmails.has(m.user.email) ||
              selectedAdmins.some((a) => a.value === m.user.email),
          ),
        );
        teamMembersOriginRef.current.delete(prevId);
        prevSelectedTeamIdRef.current = null;
      }
      return;
    }

    const newId = Number((opt as any).id ?? (opt as any).value);

    const prevId = prevSelectedTeamIdRef.current;
    if (prevId != null && prevId !== newId) {
      const prevEmails =
        teamMembersOriginRef.current.get(prevId) ?? new Set<string>();
      setAddedMembers((prev) =>
        prev.filter(
          (m) =>
            !prevEmails.has(m.user.email) ||
            selectedAdmins.some((a) => a.value === m.user.email),
        ),
      );
      teamMembersOriginRef.current.delete(prevId);
    }

    const data = await queryClient.fetchQuery({
      queryKey: ["teamDataUserList", newId],
      queryFn: () => fetchTeamData(newId),
      staleTime: 30_000,
    });

    const roles = (data?.roles ?? []) as IRole[];

    const currentEmails = new Set<string>();
    setAddedMembers((prev) => {
      prev.forEach((m) => currentEmails.add(m.user.email));
      return prev;
    });

    const toAdd: IRole[] = roles
      .filter((r) => !currentEmails.has(r.user.email))
      .map((r) => ({
        id: null,
        right: DEFAULT_ROLE_LABEL,
        right_id: DEFAULT_ROLE_ID,
        user: {
          name: r.user.name || r.user.email,
          email: r.user.email,
          id: r.user.id ?? null,
        },
        created_at: null,
      }));

    setAddedMembers((prev) => [...prev, ...toAdd]);

    const setForNew =
      teamMembersOriginRef.current.get(newId) ?? new Set<string>();
    toAdd.forEach((m) => setForNew.add(m.user.email));
    teamMembersOriginRef.current.set(newId, setForNew);

    prevSelectedTeamIdRef.current = newId;
  };

  const handleAddTeam = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const teamPayload: ITeam = {
      id: teamData ? teamData.id : "",
      name: teamName,
      regulators: selectedRegulator || [{ id: 0, name: "", code: "" }],
      roles: addedMembers,
      requests_limit: requestsLimit === "" ? undefined : Number(requestsLimit),
    };

    handleCreateEditTeam(teamPayload, !!teamData);
    setShowModal(false);
  };

  const handleAdminOnChange = (newValue: ReadonlyArray<IOldUser>) => {
    const newAdmins = Array.from(newValue);
    setSelectedAdmins(newAdmins);

    setAddedMembers((prevMembers) => {
      let updatedMembers = [...prevMembers];

      updatedMembers = updatedMembers.map((m) => {
        const isNowAdmin = newAdmins.some((a) => a.value === m.user.email);
        if (isNowAdmin) {
          return {
            ...m,
            right: roleOptions[0].label, // Admin
            right_id: Number(roleOptions[0].value),
          };
        } else {
          return {
            ...m,
            right: roleOptions[2].label, // Viewer
            right_id: Number(roleOptions[2].value),
          };
        }
      });

      newAdmins.forEach((admin) => {
        const exists = updatedMembers.some((m) => m.user.email === admin.value);
        if (!exists) {
          updatedMembers.unshift({
            id: null,
            right: roleOptions[0].label,
            right_id: Number(roleOptions[0].value),
            user: {
              name: admin.label,
              email: admin.value,
              id: null,
            },
            created_at: null,
          });
        }
      });

      return updatedMembers;
    });
  };

  const handleRoleChange = (option: IOption, member: IRole) => {
    setAddedMembers((prev) =>
      prev.map((m) =>
        m.user.email === member.user.email
          ? { ...m, right_id: Number(option.value), right: option.label }
          : m,
      ),
    );

    if (option.label === "Admin") {
      setSelectedAdmins((prev) => {
        if (!prev.some((admin) => admin.value === member.user.email)) {
          return [
            ...prev,
            {
              value: member.user.email,
              label: member.user.name || member.user.email,
            },
          ];
        }
        return prev;
      });
    } else {
      setSelectedAdmins((prev) =>
        prev.filter((admin) => admin.value !== member.user.email),
      );
    }
  };

  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  return (
    <div className="modal-overlay">
      <form
        onSubmit={handleAddTeam}
        className="modal-panel p-4 w-[600px] relative"
      >
        <h2 className="text-xl title-base-color text-center mb-4">
          {!teamData ? t("adminPanel.createNewTeam") : t("adminPanel.editTeam")}
        </h2>
        <button
          aria-label="Close"
          className="absolute right-2 top-3 z-10 modal-close-btn"
          onClick={() => setShowModal(false)}
          type="button"
        >
          <FaTimes className="text-xl text-gray-500 hover:text-del-red" />
        </button>
        <div className="mb-4">
          <label className="block title-base-color mb-1">
            {t("adminPanel.teamName")}{" "}
            <span className="text-del-red dark:text-red-400">*</span>
          </label>
          <input
            type="text"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            required
            className={`w-full px-3 py-1.5 border rounded focus:outline-none focus:ring-2 text-sm ${
              darkMode
                ? "bg-gray-700 text-gray-300 border-gray-600 focus:ring-green-500"
                : "bg-gray-200 text-gray-700 border-gray-300 focus:ring-green-400"
            }`}
            placeholder={t("adminPanel.teamNamePlaceholder")}
          />
        </div>

        {!teamData && (
          <div className="mb-4 text-sm">
            <label className="block title-base-color mb-1 text-base">
              {t("adminPanel.adminUser")}{" "}
              <span className="text-del-red dark:text-red-400">*</span>
            </label>
            <Select<IOldUser, true>
              isMulti
              isSearchable
              options={adminOptions}
              value={selectedAdmins}
              onChange={handleAdminOnChange}
              classNamePrefix="react-select"
              placeholder={t("selectPlaceholder")}
              styles={getSelectStyles({ menuPortal: (base: any) => ({ ...base, zIndex: 200000 }) })}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
            />
          </div>
        )}

        <div className="mb-4 text-sm">
          <label className="block title-base-color mb-1 text-base">
            {t("adminPanel.regulators")}{" "}
            <span className="text-del-red dark:text-red-400">*</span>
          </label>
          <Select
            isMulti
            isSearchable
            options={regulatorOptions}
            value={selectedRegulator?.map((r) => ({
              value: r.id.toString(),
              label: r.name,
            }))}
            onChange={(options) => {
              const newRegulators = options.map(
                (option: { value: string; label: string }) => ({
                  id: option.value,
                  name: option.label,
                  code: "",
                }),
              );
              setSelectedRegulator(newRegulators);
            }}
            required
            classNamePrefix="react-select"
            placeholder={t("selectPlaceholder")}
            styles={getSelectStyles({ menuPortal: (base: any) => ({ ...base, zIndex: 200000 }) })}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
          />
        </div>

        <div className="mb-4 text-sm">
          <label className="block title-base-color mb-1 text-base">
            {t("adminPanel.chatLimitPerUser")}
          </label>
          <input
            type="number"
            min={0}
            step={1}
            value={requestsLimit}
            onChange={(e) => setRequestsLimit(e.target.value)}
            disabled={!user.is_superuser}
            className={`w-full px-3 py-1.5 border rounded focus:outline-none focus:ring-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed ${
              darkMode
                ? "bg-gray-700 text-gray-300 border-gray-600 focus:ring-green-500"
                : "bg-gray-200 text-gray-700 border-gray-300 focus:ring-green-400"
            }`}
          />
        </div>

        {!teamData && (
          <>
            <div className="flex space-x-2 text-sm">
              <div className="w-1/2">
                <label className="block title-base-color mb-1 text-base">
                  {t("adminPanel.teamMembers")}
                </label>
                <CreatableSelect
                  isSearchable
                  value={null}
                  options={memberOptions.filter(
                    (member) =>
                      !addedMembers.some(
                        (added) => added.user.email === member.value,
                      ),
                  )}
                  onChange={(newValue: any) => {
                    if (newValue) {
                      handleAddMember(newValue);
                    }
                  }}
                  classNamePrefix="react-select"
                  placeholder={t("selectPlaceholder")}
                  styles={getSelectStyles({ menuPortal: (base: any) => ({ ...base, zIndex: 200000 }) })}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
              <div className="w-1/2">
                <label className="block title-base-color mb-1 text-base">
                  {t("adminPanel.otherTeamMembers")}
                </label>
                <Select
                  isSearchable
                  isClearable
                  options={
                    teamOptions.filter(
                      (team) => team.value !== teamData?.id,
                    ) as any
                  }
                  value={selectedTeam}
                  onChange={handleCopyTeamMembers}
                  classNamePrefix="react-select"
                  placeholder={t("selectPlaceholder")}
                  styles={getSelectStyles({ menuPortal: (base: any) => ({ ...base, zIndex: 200000 }) })}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
            </div>

            {addedMembers.length > 0 && (
              <div className="mt-4 max-h-52 overflow-y-auto">
                <ul>
                  {addedMembers.map((member) => (
                    <li
                        key={member.user.id}
                        className="flex items-center justify-between mb-2 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded min-h-[44px]"
                      >
                      <span className="text-sm text-base-color">
                        {member.user.name || member.user.email}
                      </span>
                      <div className="flex items-center space-x-2 text-sm">
                        <Select
                          value={{
                            value: member.right_id?.toString(),
                            label: member.right,
                          }}
                          options={roleOptions}
                          onChange={(option: any) =>
                            handleRoleChange(option, member)
                          }
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          classNamePrefix="react-select"
                          styles={getSelectStyles({
                            option: (base: any, { isFocused }: any) => ({
                              ...base,
                              backgroundColor: isFocused
                                ? "#86BC25"
                                : darkMode
                                  ? "#374151"
                                  : "#FFFFFF",
                              color: darkMode ? "#FFFFFF" : "#000000",
                              fontSize: "14px",
                            }),
                            menuPortal: (base: any) => ({ ...base, zIndex: 200000 }),
                            menuPosition: "fixed",
                          })}
                          menuPosition="fixed"
                        />
                        <hr className="h-7 border-l border-gray-300 dark:border-gray-600 mx-2" />
                        <DeleteButton
                          onClick={() => handleRemoveMember(member)}
                          className="icon-gray tooltip-delete-prompt focus:outline-none text-del-red hover:text-red-700"
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end space-x-4 mt-4 text-sm">
          <button
            type="button"
            onClick={() => setShowModal(false)}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            {t("adminPanel.cancel")}
          </button>
          <button
            type="submit"
            disabled={!teamName && !selectedAdmins && !selectedRegulator}
            className={`px-4 py-2 ${teamName && selectedAdmins && selectedRegulator ? "btn-green" : "btn-disabled"}`}
          >
            {t("adminPanel.save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export { CreateEditTeamModal };
